/**
 * ProjectWorkOrdersPage - List view for Project Work Orders.
 *
 * Project WOs are parent groupings that own a pool of hours and zero-or-
 * more child WorkOrders. This page surfaces the per-project allocation
 * progress (X / Y hrs) so a manager can spot over- or under-allocated
 * projects at a glance and drill in to adjust.
 *
 * Role-gated to admin / super_admin via App.tsx routing. The backend
 * also scopes by accessible companies; a non-privileged user who
 * reaches this URL directly sees an empty list rather than a 403.
 *
 * @route /project-work-orders
 */
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { projectWorkOrderService } from '../../services/projectWorkOrderService';
import type { ProjectWorkOrder } from '../../services/projectWorkOrderService';
import { camelizeKeys } from '../../services/api';
import { qk } from '../../lib/queryKeys';
import { Spinner } from '../../components/atoms/Spinner';
import { Button, ActionMenu, Alert } from '../../components/atoms';
import { PageContainer } from '../../components/templates/PageContainer';

/** Visual status badge classes — mirrors the WO list page palette so
 *  the two surfaces feel like one family. */
const STATUS_BADGE_COLORS: Record<string, string> = {
	draft: 'bg-gray-100 text-gray-700 border border-gray-300',
	active: 'bg-blue-100 text-blue-700 border border-blue-300',
	completed: 'bg-green-100 text-green-700 border border-green-300',
	cancelled: 'bg-gray-200 text-gray-500 border border-gray-300',
};

const ProjectWorkOrdersPage: React.FC = () => {
	const navigate = useNavigate();
	const { hasAnyRole } = useAuth();

	const canCreate = hasAnyRole(['super_admin', 'admin']);

	const query = useQuery({
		queryKey: qk.projectWorkOrders.list(),
		queryFn: async () => {
			const res = await projectWorkOrderService.list();
			/* Server returns a Laravel paginator with snake_case keys.
			 * api.ts does not camelize, so do it locally before reading
			 * camelCase fields off the rows. */
			const raw = (res.data as any)?.data ?? [];
			const rows = camelizeKeys(raw) as ProjectWorkOrder[];
			return rows;
		},
	});

	const projects: ProjectWorkOrder[] = query.data ?? [];
	const isLoading = query.isLoading;
	const error = query.error ? (query.error as Error).message : null;

	return (
		<PageContainer block="project-work-orders-page" dataRef="page-project-work-orders-page">
			{/* --- Header --- */}
			<div className="flex flex-col tablet:flex-row tablet:items-center tablet:justify-between gap-12px mb-24px">
				<div>
					<h1 className="page-title text-24px font-heading text-brand-heading m-0">Project Work Orders</h1>
					<p className="text-14px text-brand-text-muted mt-4px">Group related work orders under a project and track hours against a shared total.</p>
				</div>
				{canCreate && (
					<Button variant="primary" onClick={() => navigate('/project-work-orders/create')}>+ New Project</Button>
				)}
			</div>

			{/* --- Error --- */}
			{error && (
				<Alert variant="error" className="mb-16px">{error}</Alert>
			)}

			{/* --- Loading --- */}
			{isLoading && (
				<div className="px-16px py-32px flex items-center justify-center"><Spinner size="md" label="Loading projects" /></div>
			)}

			{/* --- Empty --- */}
			{!isLoading && !error && projects.length === 0 && (
				<div className="px-16px py-32px text-center text-14px text-brand-text-muted border border-dashed border-brand-border rounded-md">
					{canCreate ? 'No projects yet. Create your first project to start allocating hours across work orders.' : 'No projects assigned to your company yet.'}
				</div>
			)}

			{/* --- Table (tablet+) --- */}
			{!isLoading && !error && projects.length > 0 && (
				<div className="hidden tablet:block overflow-x-auto border border-brand-border rounded-md bg-white">
					<table className="w-full text-14px">
						<thead className="bg-brand-surface text-brand-text-muted text-12px uppercase tracking-wide">
							<tr>
								<th className="text-left px-16px py-12px font-medium">Project</th>
								<th className="text-left px-16px py-12px font-medium">Status</th>
								<th className="text-left px-16px py-12px font-medium">Hours (Allocated / Total)</th>
								<th className="text-left px-16px py-12px font-medium">Schedule</th>
								<th className="text-left px-16px py-12px font-medium">Updated</th>
								{/* Per-row kebab — View / Edit / Duplicate. Width matches
								    the WO list and Inspection list so the column lines up
								    visually across the three surfaces. */}
								<th className="text-left px-16px py-12px font-medium w-48px"></th>
							</tr>
						</thead>
						<tbody>
							{projects.map((p) => {
								/* Hours are computed server-side and attached to
								 * every list row. The pool is charged for work
								 * actually closed out (hoursActualUsed); open
								 * estimates are earmarked, not spent. The row
								 * shows used/total and flags the projection when
								 * outstanding estimates would overrun the pool. */
								const used = Number(p.hoursActualUsed ?? 0);
								const total = Number(p.totalHours ?? 0);
								const estimatedOpen = Number(p.hoursEstimatedOpen ?? 0);
								const remaining = Number(p.hoursRemaining ?? total - used);
								const projected = Number(p.hoursProjectedRemaining ?? remaining - estimatedOpen);
								const overAllocated = projected < 0;
								return (
									<tr key={p.id} className="border-t border-brand-border hover:bg-brand-surface/40 cursor-pointer" onClick={() => navigate(`/project-work-orders/${p.id}`)}>
										<td className="px-16px py-12px">
											<div className="font-semibold text-brand-text">{p.name}</div>
											{p.description && <div className="text-12px text-brand-text-muted mt-2px line-clamp-1">{p.description}</div>}
										</td>
										<td className="px-16px py-12px">
											<span className={['inline-flex items-center px-8px py-2px rounded-full text-12px font-semibold uppercase tracking-wide whitespace-nowrap', STATUS_BADGE_COLORS[p.status] || STATUS_BADGE_COLORS.draft].join(' ')}>
												{p.status}
											</span>
										</td>
										<td className="px-16px py-12px">
											<div className="text-14px text-brand-text">{used} / {total} hrs used</div>
											<div className={['text-12px mt-2px', overAllocated ? 'text-status-error font-semibold' : 'text-brand-text-muted'].join(' ')}>
												{overAllocated ? `Projected over by ${Math.abs(projected)}` : `${remaining} left · ${estimatedOpen} est. open`}
											</div>
										</td>
										<td className="px-16px py-12px text-12px text-brand-text-muted">
											{p.startDate ? new Date(p.startDate).toLocaleDateString() : '—'}
											{p.endDate ? ` – ${new Date(p.endDate).toLocaleDateString()}` : ''}
										</td>
										<td className="px-16px py-12px text-12px text-brand-text-muted">{new Date(p.updatedAt).toLocaleDateString()}</td>
										{/* Actions cell — mirror the WO list and Inspection list
										    UI exactly. stopPropagation on the wrapper so the kebab
										    click never triggers the row's navigate-to-detail
										    behavior. Edit / Duplicate are gated to roles that can
										    create projects (admin / super_admin); View is open to
										    anyone who can see the list. */}
										<td className="px-16px py-12px" onClick={(e) => e.stopPropagation()}>
											<ActionMenu
												ariaLabel={`Actions for project ${p.id}`}
												items={[
													{ label: 'View', onClick: () => navigate(`/project-work-orders/${p.id}`) },
													canCreate && { label: 'Edit', onClick: () => navigate(`/project-work-orders/${p.id}/edit`) },
													canCreate && { label: 'Duplicate', onClick: () => navigate(`/project-work-orders/create?duplicate=${p.id}`) },
												]}
											/>
										</td>
									</tr>
								);
							})}
						</tbody>
					</table>
				</div>
			)}

			{/* --- Card list (mobile) --- */}
			{!isLoading && !error && projects.length > 0 && (
				<div className="tablet:hidden flex flex-col gap-12px">
					{projects.map((p) => {
						/* Mirror the table's hours math so the two surfaces
						 * stay in sync. */
						const used = Number(p.hoursActualUsed ?? 0);
						const total = Number(p.totalHours ?? 0);
						const estimatedOpen = Number(p.hoursEstimatedOpen ?? 0);
						const remaining = Number(p.hoursRemaining ?? total - used);
						const projected = Number(p.hoursProjectedRemaining ?? remaining - estimatedOpen);
						const overAllocated = projected < 0;
						return (
							<div
								key={p.id}
								className="bg-white border border-brand-border rounded-12px p-16px cursor-pointer"
								onClick={() => navigate(`/project-work-orders/${p.id}`)}
							>
								<div className="flex items-start justify-between gap-8px">
									<div className="min-w-0">
										<div className="font-semibold text-brand-text">{p.name}</div>
										{p.description && <div className="text-12px text-brand-text-muted mt-2px line-clamp-2">{p.description}</div>}
									</div>
									{/* Actions — stopPropagation so the kebab never triggers
									    the card's navigate-to-detail behavior. */}
									<div className="shrink-0" onClick={(e) => e.stopPropagation()}>
										<ActionMenu
											ariaLabel={`Actions for project ${p.id}`}
											items={[
												{ label: 'View', onClick: () => navigate(`/project-work-orders/${p.id}`) },
												canCreate && { label: 'Edit', onClick: () => navigate(`/project-work-orders/${p.id}/edit`) },
												canCreate && { label: 'Duplicate', onClick: () => navigate(`/project-work-orders/create?duplicate=${p.id}`) },
											]}
										/>
									</div>
								</div>
								<div className="mt-8px">
									<span className={['inline-flex items-center px-8px py-2px rounded-full text-12px font-semibold uppercase tracking-wide whitespace-nowrap', STATUS_BADGE_COLORS[p.status] || STATUS_BADGE_COLORS.draft].join(' ')}>
										{p.status}
									</span>
								</div>
								<div className="mt-8px">
									<div className="text-14px text-brand-text">{used} / {total} hrs used</div>
									<div className={['text-12px mt-2px', overAllocated ? 'text-status-error font-semibold' : 'text-brand-text-muted'].join(' ')}>
										{overAllocated ? `Projected over by ${Math.abs(projected)}` : `${remaining} left · ${estimatedOpen} est. open`}
									</div>
								</div>
								<div className="mt-8px text-12px text-brand-text-muted">
									<span className="font-medium text-brand-text">Schedule: </span>
									{p.startDate ? new Date(p.startDate).toLocaleDateString() : '—'}
									{p.endDate ? ` – ${new Date(p.endDate).toLocaleDateString()}` : ''}
								</div>
								<div className="mt-4px text-12px text-brand-text-muted">
									<span className="font-medium text-brand-text">Updated: </span>
									{new Date(p.updatedAt).toLocaleDateString()}
								</div>
							</div>
						);
					})}
				</div>
			)}
		</PageContainer>
	);
};

export default ProjectWorkOrdersPage;
